// Items: base types, affixes, rarity rolls, legendaries. Loot is only ever rolled on the
// server, but the tables live in shared code so the client can present them consistently.

import { ClassId, Item, Mods, Rarity, SlotId, StatKey } from './types';

export interface RNGLike {
  next(): number;
  int(min: number, max: number): number;
  pick<T>(a: readonly T[]): T;
  chance(p: number): boolean;
}

export interface ItemBase {
  id: string;
  slot: SlotId | 'ring';
  names: string[]; // tier names: index grows with item level
  icon: string;
  kind: 'weapon' | 'armor' | 'jewel' | 'offhand';
  classes?: ClassId[];
  /** Weapon damage multiplier (weapons), attack feel. */
  dmgMul?: number;
  /** Armor multiplier (armor). */
  armorMul?: number;
  implicit?: Mods;
}

export const ITEM_BASES: Record<string, ItemBase> = {
  // Weapons per class
  sword: { id: 'sword', slot: 'weapon', kind: 'weapon', icon: 'sword', classes: ['reaver'], dmgMul: 1.0, names: ['Short Sword', 'Longsword', 'War Sword', 'Runeblade', 'Warpedge', 'Phase Saber'] },
  axe: { id: 'axe', slot: 'weapon', kind: 'weapon', icon: 'axe', classes: ['reaver'], dmgMul: 1.15, implicit: { atkSpd: -5 }, names: ['Hatchet', 'Battle Axe', 'Great Axe', 'Executioner', 'Worldcleaver', 'Star Axe'] },
  staff: { id: 'staff', slot: 'weapon', kind: 'weapon', icon: 'staff', classes: ['spellweaver'], dmgMul: 1.05, implicit: { mp: 10 }, names: ['Gnarled Staff', 'Battle Staff', 'Rune Staff', 'Archon Staff', 'Warp Conduit', 'Astral Rod'] },
  wand: { id: 'wand', slot: 'weapon', kind: 'weapon', icon: 'wand', classes: ['spellweaver', 'hexer'], dmgMul: 0.9, implicit: { atkSpd: 10 }, names: ['Twig Wand', 'Yew Wand', 'Bone Wand', 'Grim Wand', 'Lich Wand', 'Rift Wand'] },
  pistol: { id: 'pistol', slot: 'weapon', kind: 'weapon', icon: 'pistol', classes: ['gunner'], dmgMul: 0.92, implicit: { atkSpd: 10 }, names: ['Flintlock', 'Revolver', 'Autopistol', 'Hand Cannon', 'Plasma Pistol', 'Warp Blaster'] },
  rifle: { id: 'rifle', slot: 'weapon', kind: 'weapon', icon: 'rifle', classes: ['gunner'], dmgMul: 1.12, implicit: { atkSpd: -5 }, names: ['Musket', 'Carbine', 'Battle Rifle', 'Railgun', 'Pulse Rifle', 'Singularity Gun'] },
  mace: { id: 'mace', slot: 'weapon', kind: 'weapon', icon: 'mace', classes: ['cleric'], dmgMul: 1.0, names: ['Club', 'Flanged Mace', 'Morning Star', 'Sacred Mace', 'Dawnbreaker', 'Sun Scepter'] },
  scepter: { id: 'scepter', slot: 'weapon', kind: 'weapon', icon: 'scepter', classes: ['cleric'], dmgMul: 0.92, implicit: { hpRegen: 1, mp: 10 }, names: ['Censer', 'Prayer Rod', 'Holy Scepter', 'Seraph Rod', 'Halo Scepter', 'Empyrean Rod'] },
  hammer: { id: 'hammer', slot: 'weapon', kind: 'weapon', icon: 'hammer', classes: ['ironclad'], dmgMul: 1.05, implicit: { hp: 12 }, names: ['Maul', 'War Hammer', 'Great Maul', 'Thunder Maul', 'Colossus Hammer', 'Neutron Maul'] },
  scythe: { id: 'scythe', slot: 'weapon', kind: 'weapon', icon: 'scythe', classes: ['hexer'], dmgMul: 1.05, implicit: { manaSteal: 1 }, names: ['Sickle', 'Scythe', 'Grim Scythe', 'Soul Reaper', 'Harvester', 'Void Reaper'] },
  // Off-hands
  shield: { id: 'shield', slot: 'offhand', kind: 'offhand', icon: 'shield', classes: ['reaver', 'ironclad', 'cleric'], armorMul: 1.2, implicit: { dmgReduce: 2 }, names: ['Buckler', 'Kite Shield', 'Tower Shield', 'Aegis', 'Bulwark', 'Force Barrier'] },
  orb: { id: 'orb', slot: 'offhand', kind: 'offhand', icon: 'orb', classes: ['spellweaver', 'hexer', 'cleric'], armorMul: 0.3, implicit: { mp: 15, mpRegen: 0.5 }, names: ['Glass Orb', 'Sacred Globe', 'Dimensional Shard', 'Warp Sphere', 'Eldritch Orb', 'Star Fragment'] },
  mag: { id: 'mag', slot: 'offhand', kind: 'offhand', icon: 'mag', classes: ['gunner'], armorMul: 0.3, implicit: { crit: 2 }, names: ['Bandolier', 'Drum Magazine', 'Power Cell', 'Hollow Points', 'Plasma Coil', 'Warp Capacitor'] },
  // Armor
  helm: { id: 'helm', slot: 'helm', kind: 'armor', icon: 'helm', armorMul: 0.6, names: ['Cap', 'Skull Cap', 'Great Helm', 'Crown', 'Warp Visor', 'Astral Diadem'] },
  armor: { id: 'armor', slot: 'armor', kind: 'armor', icon: 'armor', armorMul: 1.5, names: ['Quilted Armor', 'Chain Mail', 'Plate Mail', 'Ornate Plate', 'Warpsteel Plate', 'Nanoweave Suit'] },
  gloves: { id: 'gloves', slot: 'gloves', kind: 'armor', icon: 'gloves', armorMul: 0.35, names: ['Leather Gloves', 'Chain Gloves', 'Gauntlets', 'War Gauntlets', 'Warp Grips', 'Servo Claws'] },
  boots: { id: 'boots', slot: 'boots', kind: 'armor', icon: 'boots', armorMul: 0.35, implicit: { moveSpd: 2 }, names: ['Sandals', 'Leather Boots', 'Greaves', 'War Boots', 'Warp Treads', 'Grav Boots'] },
  // Jewelry
  ring: { id: 'ring', slot: 'ring', kind: 'jewel', icon: 'ring', names: ['Ring'] },
  amulet: { id: 'amulet', slot: 'amulet', kind: 'jewel', icon: 'amulet', names: ['Amulet'] },
};

export function weaponDamage(ilvl: number, mul = 1): [number, number] {
  const avg = (4 + 1.3 * ilvl) * mul;
  const lo = Math.max(1, Math.round(avg * 0.75));
  const hi = Math.max(lo + 1, Math.round(avg * 1.25));
  return [lo, hi];
}

export function baseArmor(ilvl: number, mul = 1): number {
  return Math.max(1, Math.round((3 + 1.5 * ilvl) * mul));
}

/** Which of the six tiers an item level falls in. Names and looks both step with it. */
export function itemTier(ilvl: number): number {
  return Math.max(0, Math.min(5, Math.floor(ilvl / 8)));
}

function tierName(base: ItemBase, ilvl: number): string {
  const idx = Math.min(base.names.length - 1, itemTier(ilvl));
  return base.names[idx];
}

type AffixDef = {
  key: StatKey;
  /** [min,max] at item level 1. */
  lo: [number, number];
  /** [min,max] added per item level. */
  per: [number, number];
  /** Slots the affix can land on. Undefined means any. */
  slots?: (SlotId | 'ring')[];
  kinds?: ('weapon' | 'armor' | 'jewel' | 'offhand')[];
  /** Minimum item level. */
  min?: number;
  prefix?: string;
  suffix?: string;
  weight?: number;
};

const AFFIXES: AffixDef[] = [
  { key: 'dmgPct', lo: [8, 15], per: [0.8, 1.6], kinds: ['weapon', 'jewel'], prefix: 'Cruel', weight: 3 },
  { key: 'minDmg', lo: [1, 2], per: [0.15, 0.3], kinds: ['weapon', 'jewel', 'offhand'], prefix: 'Jagged' },
  { key: 'maxDmg', lo: [1, 3], per: [0.25, 0.5], kinds: ['weapon', 'jewel', 'offhand'], prefix: 'Massive' },
  { key: 'fireDmg', lo: [1, 3], per: [0.25, 0.6], kinds: ['weapon', 'jewel'], prefix: 'Fiery' },
  { key: 'coldDmg', lo: [1, 3], per: [0.25, 0.6], kinds: ['weapon', 'jewel'], prefix: 'Frozen' },
  { key: 'lightDmg', lo: [1, 4], per: [0.2, 0.75], kinds: ['weapon', 'jewel'], prefix: 'Shocking' },
  { key: 'poisonDmg', lo: [1, 3], per: [0.25, 0.6], kinds: ['weapon', 'jewel'], prefix: 'Venomous' },
  { key: 'atkSpd', lo: [5, 10], per: [0.1, 0.2], slots: ['weapon', 'gloves', 'ring', 'amulet'], suffix: 'of Haste' },
  { key: 'crit', lo: [2, 4], per: [0.05, 0.1], slots: ['weapon', 'gloves', 'ring', 'amulet', 'offhand'], suffix: 'of Precision' },
  { key: 'critDmg', lo: [10, 20], per: [0.4, 0.8], slots: ['weapon', 'gloves', 'amulet'], suffix: 'of Slaughter' },
  { key: 'lifeSteal', lo: [1, 3], per: [0.03, 0.08], slots: ['weapon', 'gloves', 'ring', 'amulet'], suffix: 'of the Leech', min: 5 },
  { key: 'manaSteal', lo: [1, 3], per: [0.03, 0.08], slots: ['weapon', 'gloves', 'ring', 'amulet'], suffix: 'of the Bat', min: 5 },
  { key: 'armor', lo: [3, 8], per: [0.6, 1.4], kinds: ['armor', 'offhand'], prefix: 'Sturdy', weight: 2 },
  { key: 'armorPct', lo: [8, 15], per: [0.4, 1], kinds: ['armor', 'offhand'], prefix: 'Fortified', weight: 2 },
  { key: 'hp', lo: [6, 14], per: [1, 2.2], prefix: 'Vital', weight: 3 },
  { key: 'mp', lo: [5, 12], per: [0.6, 1.3], prefix: 'Mystic', weight: 2 },
  { key: 'hpRegen', lo: [1, 2], per: [0.08, 0.18], kinds: ['armor', 'jewel', 'offhand'], suffix: 'of Renewal' },
  { key: 'mpRegen', lo: [0.5, 1.5], per: [0.05, 0.12], kinds: ['jewel', 'offhand', 'weapon'], suffix: 'of Clarity' },
  { key: 'str', lo: [2, 4], per: [0.2, 0.45], suffix: 'of Might' },
  { key: 'dex', lo: [2, 4], per: [0.2, 0.45], suffix: 'of Finesse' },
  { key: 'vit', lo: [2, 4], per: [0.2, 0.45], suffix: 'of the Titan' },
  { key: 'ene', lo: [2, 4], per: [0.2, 0.45], suffix: 'of the Mind' },
  { key: 'resFire', lo: [5, 12], per: [0.2, 0.5], kinds: ['armor', 'jewel', 'offhand'], suffix: 'of Embers' },
  { key: 'resCold', lo: [5, 12], per: [0.2, 0.5], kinds: ['armor', 'jewel', 'offhand'], suffix: 'of Winter' },
  { key: 'resLight', lo: [5, 12], per: [0.2, 0.5], kinds: ['armor', 'jewel', 'offhand'], suffix: 'of Storms' },
  { key: 'resPoison', lo: [5, 12], per: [0.2, 0.5], kinds: ['armor', 'jewel', 'offhand'], suffix: 'of Antidotes' },
  { key: 'resAll', lo: [3, 6], per: [0.1, 0.25], kinds: ['armor', 'jewel', 'offhand'], suffix: 'of the Warp', min: 12, weight: 0.6 },
  { key: 'moveSpd', lo: [5, 10], per: [0.1, 0.2], slots: ['boots'], suffix: 'of Speed', weight: 3 },
  { key: 'magicFind', lo: [6, 14], per: [0.3, 0.6], kinds: ['armor', 'jewel'], prefix: "Seeker's" },
  { key: 'goldFind', lo: [10, 25], per: [0.5, 1], kinds: ['armor', 'jewel', 'offhand'], prefix: 'Gilded' },
  { key: 'allSkills', lo: [1, 1], per: [0.02, 0.05], slots: ['weapon', 'amulet', 'offhand', 'helm'], prefix: 'Arcane', min: 15, weight: 0.35 },
  { key: 'thorns', lo: [2, 5], per: [0.4, 0.9], kinds: ['armor', 'offhand'], suffix: 'of Thorns' },
  { key: 'dmgReduce', lo: [2, 3], per: [0.04, 0.08], slots: ['armor', 'offhand', 'helm', 'amulet'], suffix: 'of Warding', min: 8, weight: 0.8 },
  { key: 'cdr', lo: [3, 5], per: [0.04, 0.1], slots: ['helm', 'amulet', 'ring', 'offhand'], suffix: 'of Alacrity', min: 10, weight: 0.8 },
];

/** Grammar used for rare item names. */
const RARE_A = ['Grim', 'Doom', 'Blood', 'Ghoul', 'Storm', 'Warp', 'Rift', 'Dread', 'Star', 'Void', 'Soul', 'Bone', 'Ember', 'Rune', 'Pale', 'Iron', 'Plague', 'Viper', 'Sun', 'Moon'];
const RARE_B: Record<string, string[]> = {
  weapon: ['Bite', 'Song', 'Edge', 'Fang', 'Scourge', 'Thirst', 'Wrath', 'Kiss'],
  offhand: ['Ward', 'Bastion', 'Aegis', 'Eye', 'Heart'],
  helm: ['Visage', 'Crown', 'Brow', 'Mask'],
  armor: ['Shroud', 'Mantle', 'Carapace', 'Hide'],
  gloves: ['Grasp', 'Touch', 'Fist', 'Hand'],
  boots: ['Stride', 'Tread', 'Step', 'Heel'],
  ring: ['Loop', 'Band', 'Coil', 'Whorl'],
  amulet: ['Charm', 'Sigil', 'Talisman', 'Star'],
};

function rollAffix(a: AffixDef, ilvl: number, rng: RNGLike): number {
  const lo = a.lo[0] + a.per[0] * ilvl;
  const hi = a.lo[1] + a.per[1] * ilvl;
  let v = lo + (hi - lo) * rng.next();
  if (a.key === 'allSkills') {
    return Math.max(1, Math.min(3, Math.floor(v)));
  }
  if (a.key === 'hpRegen' || a.key === 'mpRegen') {
    v = Math.round(v * 10) / 10;
  } else {
    v = Math.round(v);
  }
  return Math.max(1, v);
}

function eligibleAffixes(base: ItemBase, ilvl: number): AffixDef[] {
  return AFFIXES.filter((a) => {
    if (a.min && ilvl < a.min) return false;
    if (a.slots && !a.slots.includes(base.slot)) return false;
    if (a.kinds && !a.kinds.includes(base.kind)) return false;
    return true;
  });
}

let uidCounter = 0;
export function newUid(rng: RNGLike): string {
  uidCounter = (uidCounter + 1) % 1e6;
  return `${Date.now().toString(36)}${uidCounter.toString(36)}${Math.floor(rng.next() * 1e6).toString(36)}`;
}

function itemValue(rarity: Rarity, ilvl: number, numAffix: number): number {
  return Math.round((5 + ilvl * 3) * (1 + numAffix * 0.5) * [1, 2, 4, 10][rarity]);
}

export function classItemBases(cls: ClassId): ItemBase[] {
  return Object.values(ITEM_BASES).filter((b) => !b.classes || b.classes.includes(cls));
}

export function canUseItem(item: Item, cls: ClassId): boolean {
  const base = ITEM_BASES[item.base];
  return !base?.classes || base.classes.includes(cls);
}

export function rollRarity(rng: RNGLike, magicFind: number, tierBonus: number): Rarity {
  // tierBonus: 0 normal, 1 champion, 2 unique, 3 boss
  const mf = 1 + magicFind / 100;
  const r = rng.next();
  const legendary = (0.004 + 0.012 * tierBonus + 0.018 * Math.max(0, tierBonus - 2)) * mf;
  const rare = (0.05 + 0.1 * tierBonus) * mf;
  const magic = (0.28 + 0.14 * tierBonus) * Math.sqrt(mf);
  if (r < legendary) return 3;
  if (r < legendary + rare) return 2;
  if (r < legendary + rare + magic) return 1;
  return 0;
}

export function generateItem(rng: RNGLike, ilvl: number, cls: ClassId, rarity: Rarity): Item {
  if (rarity === 3) {
    const leg = rollLegendary(rng, ilvl, cls);
    if (leg) return leg;
    rarity = 2;
  }
  const bases = classItemBases(cls).filter((b) => (rarity === 0 ? b.kind !== 'jewel' : true));
  // Weight weapons/armor up a little vs jewelry.
  const base = pickWeightedBase(bases, rng);
  return buildItem(rng, base, ilvl, rarity);
}

function pickWeightedBase(bases: ItemBase[], rng: RNGLike): ItemBase {
  const w = (b: ItemBase) => (b.kind === 'weapon' ? 1.6 : b.kind === 'jewel' ? 0.7 : 1);
  let total = 0;
  for (const b of bases) total += w(b);
  let r = rng.next() * total;
  for (const b of bases) {
    r -= w(b);
    if (r <= 0) return b;
  }
  return bases[bases.length - 1];
}

export function buildItem(rng: RNGLike, base: ItemBase, ilvl: number, rarity: Rarity): Item {
  const mods: Mods = { ...(base.implicit ?? {}) };
  const numAffix = rarity === 0 ? 0 : rarity === 1 ? rng.int(1, 2) : rng.int(3, 5);
  const pool = eligibleAffixes(base, ilvl).slice();
  let prefix = '';
  let suffix = '';
  for (let i = 0; i < numAffix && pool.length; i++) {
    let total = 0;
    for (const a of pool) total += a.weight ?? 1;
    let r = rng.next() * total;
    let idx = pool.length - 1;
    for (let j = 0; j < pool.length; j++) {
      r -= pool[j].weight ?? 1;
      if (r <= 0) {
        idx = j;
        break;
      }
    }
    const a = pool[idx];
    pool.splice(idx, 1);
    mods[a.key] = (mods[a.key] ?? 0) + rollAffix(a, ilvl, rng);
    if (a.prefix && !prefix) prefix = a.prefix;
    if (a.suffix && !suffix) suffix = a.suffix;
  }
  const bn = tierName(base, ilvl);
  let name = bn;
  if (rarity === 1) {
    name = `${prefix ? prefix + ' ' : ''}${bn}${suffix ? ' ' + suffix : ''}`;
  } else if (rarity === 2) {
    const b = RARE_B[base.slot] ?? RARE_B.weapon;
    name = `${rng.pick(RARE_A)} ${rng.pick(b)}`;
  }
  const item: Item = {
    uid: newUid(rng),
    base: base.id,
    name,
    rarity,
    ilvl,
    req: Math.max(1, Math.round(ilvl * 0.9)),
    slot: base.slot,
    icon: base.icon,
    mods,
    value: itemValue(rarity, ilvl, numAffix),
  };
  if (base.kind === 'weapon') {
    // Rarer weapons also roll better base damage.
    const bonus = [1, 1.08, 1.18, 1.3][rarity];
    item.dmg = weaponDamage(ilvl, (base.dmgMul ?? 1) * bonus);
  }
  if (base.armorMul) {
    const bonus = [1, 1.08, 1.18, 1.3][rarity];
    item.armor = baseArmor(ilvl, base.armorMul * bonus);
  }
  return item;
}

// ─── Legendaries ───

interface LegendaryDef {
  id: string;
  base: string;
  name: string;
  flavor: string;
  minLvl: number;
  mods: (ilvl: number) => Mods;
  proc?: string;
}

const LEGENDARIES: LegendaryDef[] = [
  { id: 'gorefeast', base: 'sword', name: "Gorefeast", flavor: 'It has never once been cleaned.', minLvl: 1, mods: (l) => ({ dmgPct: 40 + l * 2, lifeSteal: 8, hp: 15 + l * 2 }) },
  { id: 'kingmaker', base: 'axe', name: 'Kingmaker', flavor: 'Three crowns. One edge.', minLvl: 10, mods: (l) => ({ dmgPct: 60 + l * 2, critDmg: 40, str: 10 + l / 2 }) },
  { id: 'emberheart', base: 'staff', name: 'Emberheart', flavor: 'Warm to the touch, always.', minLvl: 1, mods: (l) => ({ dmgPct: 40 + l * 2, fireDmg: 5 + l, allSkills: 1, mp: 20 + l * 2 }) },
  { id: 'whisperthorn', base: 'wand', name: 'Whisperthorn', flavor: 'It tells you who is next.', minLvl: 5, mods: (l) => ({ dmgPct: 30 + l * 2, atkSpd: 25, manaSteal: 6, ene: 10 + l / 2 }) },
  { id: 'lastword', base: 'pistol', name: 'The Last Word', flavor: 'Argument concluded.', minLvl: 1, mods: (l) => ({ dmgPct: 40 + l * 2, crit: 12, critDmg: 40, atkSpd: 15 }) },
  { id: 'starfall', base: 'rifle', name: 'Starfall Rail', flavor: 'Recovered, still warm, from a very deep crater.', minLvl: 12, mods: (l) => ({ dmgPct: 60 + l * 2, lightDmg: 6 + l, dex: 10 + l / 2 }) },
  { id: 'dawnsong', base: 'mace', name: 'Dawnsong', flavor: 'The dead find it deafening.', minLvl: 1, mods: (l) => ({ dmgPct: 40 + l * 2, hpRegen: 3 + l / 5, resAll: 10, allSkills: 1 }) },
  { id: 'colossus', base: 'hammer', name: 'Colossus Fist', flavor: 'Forged from the knuckle of a fallen giant.', minLvl: 1, mods: (l) => ({ dmgPct: 40 + l * 2, hp: 30 + l * 3, thorns: 8 + l, str: 8 + l / 2 }) },
  { id: 'gravecaller', base: 'scythe', name: 'Gravecaller', flavor: 'It was here before the first grave.', minLvl: 1, mods: (l) => ({ dmgPct: 40 + l * 2, allSkills: 2, ene: 8 + l / 2 }) },
  { id: 'ironveil', base: 'shield', name: 'Ironveil', flavor: 'Blows slide off. So does reason.', minLvl: 1, mods: (l) => ({ armorPct: 40 + l, resAll: 15, dmgReduce: 6, hp: 15 + l * 2 }) },
  { id: 'eyeofwarp', base: 'orb', name: 'Eye of the Warp', flavor: 'Blinks when you are not looking.', minLvl: 1, mods: (l) => ({ allSkills: 1, cdr: 10, mp: 30 + l * 2, mpRegen: 2 + l / 8 }) },
  { id: 'powercell', base: 'mag', name: 'Reactor Core', flavor: 'Radiation warning sticker included.', minLvl: 1, mods: (l) => ({ crit: 10, dmgPct: 20 + l, atkSpd: 12, lightDmg: 4 + l / 2 }) },
  { id: 'crownofthorns', base: 'helm', name: 'Crown of Ages', flavor: 'It remembers every head it has sat upon.', minLvl: 1, mods: (l) => ({ allSkills: 1, resAll: 12, dmgReduce: 4, hp: 15 + l * 2 }) },
  { id: 'warplate', base: 'armor', name: 'Warpforged Plate', flavor: 'It does not quite exist where you think it does.', minLvl: 1, mods: (l) => ({ armorPct: 60 + l, hp: 30 + l * 4, resAll: 12 }) },
  { id: 'bloodgrip', base: 'gloves', name: 'Bloodgrip', flavor: 'Fingers tighten on their own.', minLvl: 1, mods: (l) => ({ atkSpd: 18, crit: 6, lifeSteal: 6, dmgPct: 10 + l }) },
  { id: 'phaseboots', base: 'boots', name: 'Phase Runners', flavor: 'You are always a little bit ahead of yourself.', minLvl: 1, mods: (l) => ({ moveSpd: 25, armor: 5 + l, resAll: 8, dex: 5 + l / 3 }) },
  { id: 'sigilring', base: 'ring', name: 'Sigil of the Nine', flavor: 'Eight are missing.', minLvl: 1, mods: (l) => ({ allSkills: 1, magicFind: 25 + l, lifeSteal: 4, manaSteal: 4 }) },
  { id: 'heartamulet', base: 'amulet', name: "Tyrant's Heart", flavor: 'Beats with yours. Eventually, instead of yours.', minLvl: 1, mods: (l) => ({ allSkills: 2, hp: 20 + l * 3, dmgPct: 15 + l }) },
];

export function rollLegendary(rng: RNGLike, ilvl: number, cls: ClassId): Item | null {
  const pool = LEGENDARIES.filter((l) => {
    const base = ITEM_BASES[l.base];
    return ilvl >= l.minLvl && (!base.classes || base.classes.includes(cls));
  });
  if (!pool.length) return null;
  const def = rng.pick(pool);
  const base = ITEM_BASES[def.base];
  const rawMods = def.mods(ilvl);
  const mods: Mods = { ...(base.implicit ?? {}) };
  for (const [k, v] of Object.entries(rawMods) as [StatKey, number][]) {
    mods[k] = (mods[k] ?? 0) + (k === 'hpRegen' || k === 'mpRegen' ? Math.round(v * 10) / 10 : Math.round(v));
  }
  const item: Item = {
    uid: newUid(rng),
    base: base.id,
    name: def.name,
    rarity: 3,
    ilvl,
    req: Math.max(1, Math.round(ilvl * 0.9)),
    slot: base.slot,
    icon: base.icon,
    mods,
    value: itemValue(3, ilvl, 4),
    flavor: def.flavor,
    proc: def.proc,
  };
  if (base.kind === 'weapon') item.dmg = weaponDamage(ilvl, (base.dmgMul ?? 1) * 1.35);
  if (base.armorMul) item.armor = baseArmor(ilvl, base.armorMul * 1.35);
  return item;
}

export function starterWeapon(rng: RNGLike, cls: ClassId): Item {
  const baseId: Record<ClassId, string> = {
    reaver: 'sword', spellweaver: 'staff', gunner: 'pistol', cleric: 'mace', ironclad: 'hammer', hexer: 'scythe',
  };
  const it = buildItem(rng, ITEM_BASES[baseId[cls]], 1, 0);
  it.req = 1;
  return it;
}

export const RARITY_NAMES = ['Common', 'Magic', 'Rare', 'Legendary'];
export const RARITY_COLORS = ['#fcfcfc', '#6888fc', '#f8d878', '#f87858'];

export const POTION_PRICE = { hp: (lvl: number) => 6 + lvl * 2, mp: (lvl: number) => 5 + lvl * 2 };
export const MAX_POTIONS = 20;

export const STAT_LABELS: Record<StatKey, (v: number) => string> = {
  str: (v) => `+${v} Might`,
  dex: (v) => `+${v} Finesse`,
  vit: (v) => `+${v} Vitality`,
  ene: (v) => `+${v} Spirit`,
  hp: (v) => `+${v} Life`,
  mp: (v) => `+${v} Mana`,
  hpRegen: (v) => `+${v} Life per sec`,
  mpRegen: (v) => `+${v} Mana per sec`,
  armor: (v) => `+${v} Armor`,
  armorPct: (v) => `+${v}% Armor`,
  minDmg: (v) => `+${v} Min Damage`,
  maxDmg: (v) => `+${v} Max Damage`,
  dmgPct: (v) => `+${v}% Damage`,
  fireDmg: (v) => `+${v} Fire Damage`,
  coldDmg: (v) => `+${v} Cold Damage`,
  lightDmg: (v) => `+${v} Lightning Dmg`,
  poisonDmg: (v) => `+${v} Poison Damage`,
  atkSpd: (v) => `${v >= 0 ? '+' : ''}${v}% Attack Speed`,
  crit: (v) => `+${v}% Crit Chance`,
  critDmg: (v) => `+${v}% Crit Damage`,
  moveSpd: (v) => `+${v}% Move Speed`,
  lifeSteal: (v) => `${v}% Life Steal`,
  manaSteal: (v) => `${v}% Mana Steal`,
  resFire: (v) => `+${v}% Fire Resist`,
  resCold: (v) => `+${v}% Cold Resist`,
  resLight: (v) => `+${v}% Lightning Res`,
  resPoison: (v) => `+${v}% Poison Resist`,
  resAll: (v) => `+${v}% All Resist`,
  magicFind: (v) => `+${v}% Magic Find`,
  goldFind: (v) => `+${v}% Gold Find`,
  allSkills: (v) => `+${v} to All Skills`,
  thorns: (v) => `Reflects ${v} Damage`,
  dmgReduce: (v) => `-${v}% Damage Taken`,
  cdr: (v) => `-${v}% Cooldowns`,
};
